import React, { useState } from "react";
import { ArrowUpRight, ChevronDown, ChevronUp } from "lucide-react";
import { USE_CASES, waLink } from "../lib/execora";
import { MORE_SERVICES } from "../lib/moreServices";

export const UseCases = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <section
      id="use-cases"
      data-testid="use-cases-section"
      className="relative py-16 md:py-24 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10 sm:mb-14">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.2em] text-[#65a30d] font-semibold uppercase">
              What people use Execora for
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight leading-[1.1]">
              Things we’ve helped people <br className="hidden sm:block" /> get done in Indiranagar.
            </h2>
          </div>
          <p className="text-slate-600 max-w-sm text-base leading-relaxed">
            From small errands to everyday needs, if it can be done at a nearby shop, we’ll help you get it done.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {USE_CASES.map((u, idx) => (
            <article
              key={u.id}
              data-testid={`use-case-card-${u.id}`}
              className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 hover:border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={u.image}
                  alt={u.title}
                  loading="lazy"
                  className="h-full w-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-white/90 backdrop-blur-sm text-[10px] font-semibold tracking-widest uppercase text-slate-900">
                  {String(idx + 1).padStart(2, "0")}
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                  {u.title}
                </h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                  {u.description}
                </p>
                <a
                  href={waLink(`Hi Execora, I'd like to request: ${u.title}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`use-case-request-${u.id}`}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-[#65a30d] transition-colors"
                >
                  Request now
                  <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={2} />
                </a>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-0.5 bg-[#84cc15] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
            </article>
          ))}
        </div>

        {/* More services toggle */}
        <div className="mt-10 sm:mt-12 flex justify-center">
          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid="more-services-toggle"
            aria-expanded={expanded}
            aria-controls="more-services-grid"
            className="group inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-slate-900 bg-white border border-slate-200 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-all"
          >
            {expanded ? (
              <>
                Show fewer services
                <ChevronUp className="h-4 w-4" strokeWidth={2.2} />
              </>
            ) : (
              <>
                Show all {MORE_SERVICES.length}+ services
                <ChevronDown className="h-4 w-4 group-hover:translate-y-0.5 transition-transform" strokeWidth={2.2} />
              </>
            )}
          </button>
        </div>

        {expanded && (
          <div
            id="more-services-grid"
            data-testid="more-services-grid"
            className="mt-10 sm:mt-12 fade-up"
          >
            <div className="text-center mb-8">
              <div className="text-xs tracking-[0.2em] text-[#65a30d] font-semibold uppercase">
                The full list
              </div>
              <h3 className="mt-2 text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
                More things we can help you with
              </h3>
              <p className="mt-2 text-slate-600 text-sm sm:text-base max-w-xl mx-auto">
                 Tap any option to start a WhatsApp request. Don’t see what you need? Just tell us — we’ll try to get it done.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {MORE_SERVICES.map((s, i) => (
                <a
                  key={s.title}
                  href={waLink(`Hi Execora, I'd like to request: ${s.title}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`more-service-${i}`}
                  className="group flex items-start gap-3 p-4 rounded-2xl bg-white border border-slate-100 hover:border-[#84cc15] hover:shadow-md transition-all"
                >
                  <div className="h-10 w-10 shrink-0 rounded-xl bg-[#84cc15]/12 flex items-center justify-center group-hover:bg-[#84cc15] transition-colors">
                    <s.icon
                      className="h-5 w-5 text-[#65a30d] group-hover:text-white transition-colors"
                      strokeWidth={2}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        {s.title}
                      </span>
                      <ArrowUpRight
                        className="h-3.5 w-3.5 text-slate-400 group-hover:text-[#65a30d] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                        strokeWidth={2}
                      />
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500 leading-relaxed line-clamp-2">
                      {s.note}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default UseCases;
